import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Stack,
  Typography,
  Alert,
  Container,
  Grid,
  Fade,
  Grow,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Campaign as CampaignIcon,
  Handshake as PartnershipIcon,
  Payments as PayoutIcon,
  Schedule as PendingIcon,
  PlayArrow as OngoingIcon,
  CheckCircle as CompletedIcon,
  Launch as LaunchIcon,
  TrendingUp as TrendingIcon,
  AttachMoney as AttachMoneyIcon,
  Search as SearchIcon,
  FilterAlt as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import API from '../../../API';
import { useActivityFilter } from '../agencyCreator';

// Activity type configurations
const ACTIVITY_CONFIGS = {
  Campaign: {
    icon: CampaignIcon,
    color: '#6366f1',
    emoji: '📋',
  },
  Partnership: {
    icon: PartnershipIcon,
    color: '#10b981',
    emoji: '🤝',
  },
  Payout: {
    icon: PayoutIcon,
    color: '#3b82f6',
    emoji: '💰',
  }
};

// Category configurations
const CATEGORY_CONFIGS = {
  all: {
    icon: TrendingIcon,
    color: 'primary',
    label: 'All'
  },
  pending: {
    icon: PendingIcon,
    color: 'warning',
    label: 'Pending'
  },
  ongoing: {
    icon: OngoingIcon,
    color: 'success',
    label: 'Ongoing'
  },
  previous: {
    icon: CompletedIcon,
    color: 'info',
    label: 'Previous'
  },
};

// Get status chip color
function getStatusChipColor(status, activityType) {
  if (activityType === 'Payout') return 'success';
  
  switch (status?.toLowerCase()) {
    case 'pitched':
    case 'pending':
      return 'warning';
    case 'active':
    case 'launched':
    case 'accepted':
      return 'info';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
}

// Generate activity details
function getActivityDetails(activity) {
  const basePrice = activity.net_amount || 0;
  const formattedPrice = `$${basePrice.toLocaleString(undefined, { 
    minimumFractionDigits: 2, 
    maximumFractionDigits: 2 
  })}`;

  switch (activity.activity_type) {
    case 'Campaign':
      return {
        primary: activity.activity_name,
        secondary: activity.campaign_status,
        tertiary: formattedPrice,
        link: `/agency/creators/${activity.creator_id}/collaborations/campaigns/${activity.activity_id}`,
        statusChip: activity.creator_status,
        date: activity.activity_date,
        hasEarnings: basePrice > 0,
        isCompleted: activity.creator_status?.toLowerCase() === 'completed',
      };

    case 'Partnership':
      return {
        primary: activity.activity_name,
        secondary: activity.creator_status,
        tertiary: formattedPrice,
        link: `/agency/creators/${activity.creator_id}/collaborations/partnerships/${activity.activity_id}`,
        statusChip: activity.creator_status,
        date: activity.activity_date,
        hasEarnings: basePrice > 0,
        isCompleted: activity.creator_status?.toLowerCase() === 'completed',
      };

    case 'Payout':
      return {
        primary: activity.activity_name,
        secondary: 'Paid',
        tertiary: formattedPrice,
        link: `/agency/creators/${activity.creator_id}/payouts`,
        statusChip: 'Completed',
        date: activity.activity_date,
        hasEarnings: basePrice > 0,
        isCompleted: true,
      };

    default:
      return {
        primary: 'Unknown activity',
        secondary: 'N/A',
        tertiary: formattedPrice,
        link: '',
        statusChip: 'Unknown',
        date: activity.activity_date,
        hasEarnings: false,
        isCompleted: false,
      };
  }
}

const ModernCard = ({ children, onClick, ...props }) => {
  const theme = useTheme();
  
  return (
    <Card
      {...props}
      sx={{
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.default, 0.8)} 100%)`,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        borderRadius: 3,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        cursor: 'pointer',
        overflow: 'hidden',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-8px) scale(1.02)',
          boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.15)}`,
          '& .card-media': {
            transform: 'scale(1.1)',
          },
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        }
      }}
      onClick={onClick}
    >
      {children}
    </Card>
  );
};

// Activity Card Component
function ActivityCard({ activity }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const details = getActivityDetails(activity);
  const config = ACTIVITY_CONFIGS[activity.activity_type];

  const handleClick = () => {
    navigate(details.link);
  };

  return (
    <ModernCard onClick={handleClick}>
      {/* Icon Header */}
      <Box sx={{ 
        position: 'relative', 
        overflow: 'hidden',
        height: 140,
        background: `linear-gradient(135deg, ${alpha(config.color, 0.1)} 0%, ${alpha(config.color, 0.05)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Typography variant="h1" sx={{ fontSize: '4rem', opacity: 0.3 }}>
          {config.emoji}
        </Typography>
        
        {/* Activity Type Badge */}
        <Box sx={{ position: 'absolute', top: 12, left: 12 }}>
          <Chip 
            label={activity.activity_type}
            size="small"
            sx={{
              backgroundColor: config.color,
              color: 'white',
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
        </Box>
      </Box>

      {/* Card Body */}
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
          <Chip
            label={details.statusChip}
            size="small"
            color={getStatusChipColor(details.statusChip, activity.activity_type)}
            sx={{
              fontWeight: 600,
              borderRadius: 2,
            }}
          />
        </Box>

        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            mb: 2,
            fontSize: '1.1rem',
            lineHeight: 1.3,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {details.primary}
        </Typography>

        <Stack spacing={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <Typography variant="body2" noWrap>
              {details.secondary}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <AttachMoneyIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
              {details.tertiary}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            <Typography variant="caption">
              {new Date(details.date).toLocaleDateString()}
            </Typography>
          </Box>
        </Stack>
      </CardContent>

      {/* Card Actions */}
      <CardActions
        sx={{
          justifyContent: 'flex-end',
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          px: 2,
          py: 1.5,
          background: alpha(theme.palette.background.default, 0.3)
        }}
      >
        <Button
          size="small"
          endIcon={<LaunchIcon />}
          sx={{ fontWeight: 600 }}
        >
          View Details
        </Button>
      </CardActions>
    </ModernCard>
  );
}

// Empty state
function EmptyState({ category, headerFilter }) {
  const config = CATEGORY_CONFIGS[category];
  const Icon = config.icon;

  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      <Icon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
      <Typography variant="h6" color="text.secondary" gutterBottom>
        No {config.label.toLowerCase()} activities
      </Typography>
      <Typography variant="body2" color="text.disabled">
        {headerFilter ? (
          `No activities match the current filter`
        ) : (
          <>
            {category === 'pending' && "No new activities require attention"}
            {category === 'ongoing' && "No activities are currently in progress"}
            {category === 'previous' && "No completed activities to show"}
            {category === 'all' && "No activities found for this creator"}
          </>
        )}
      </Typography>
    </Box>
  );
}

export default function AgencyCreatorAllActivity() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const { creatorId } = useParams();
  const { activeFilter, clearFilter } = useActivityFilter();

  // Fetch activity data for specific creator
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agency', 'creator-activity', creatorId, activeTab],
    queryFn: () => API.agency.getCreatorActivity(creatorId, { filter: activeTab }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
    enabled: !!creatorId,
  });

  const activities = data?.activities || [];
  const categoryCounts = data?.category_counts || {};

  // Apply header filter when it changes
  useEffect(() => {
    if (activeFilter) {
      setActiveTab('all');
    }
  }, [activeFilter]);

  // Filter activities by search AND header filter
  const filteredActivities = useMemo(() => {
    let filtered = activities;

    // Apply search filter
    if (searchQuery) {
      const searchLower = searchQuery.toLowerCase();
      filtered = filtered.filter(activity => {
        const details = getActivityDetails(activity);
        return details.primary.toLowerCase().includes(searchLower) ||
               details.secondary.toLowerCase().includes(searchLower);
      });
    }

    // Apply header filter
    if (activeFilter) {
      filtered = filtered.filter(activity => {
        const details = getActivityDetails(activity);
        
        switch (activeFilter) {
          case 'campaigns':
            return activity.activity_type === 'Campaign' || activity.activity_type === 'Partnership';
          case 'earnings':
            return details.hasEarnings;
          case 'completed':
            return details.isCompleted;
          default:
            return true;
        }
      });
    }

    return filtered;
  }, [activities, searchQuery, activeFilter]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getFilterLabel = () => {
    switch (activeFilter) {
      case 'campaigns': return 'Campaigns & Partnerships';
      case 'earnings': return 'With Earnings';
      case 'completed': return 'Completed';
      default: return '';
    }
  };

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert 
          severity="error" 
          action={
            <Button size="small" onClick={refetch}>
              Retry
            </Button>
          }
        >
          Failed to load activity feed: {error?.response?.data?.error || error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Fade in timeout={600}>
        <Box>
          {/* Consolidated Header */}
          <Paper 
            elevation={0} 
            sx={{ 
              mb: 3,
              background: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            {/* Title Row */}
            <Box sx={{ 
              px: 3, 
              pt: 2.5, 
              pb: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    All Activity
                  </Typography>
                  <Chip
                    label={filteredActivities.length}
                    size="small"
                    sx={{ 
                      fontWeight: 600,
                      height: 24,
                    }}
                  />
                  {activeFilter && (
                    <Chip
                      icon={<FilterIcon sx={{ fontSize: 16 }} />}
                      label={getFilterLabel()}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={clearFilter}
                      sx={{ 
                        fontWeight: 500,
                        height: 24,
                      }}
                    />
                  )}
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  {Object.entries(categoryCounts)
                    .filter(([key]) => key !== 'all')
                    .map(([key, count]) => {
                      const config = CATEGORY_CONFIGS[key];
                      return (
                        <Chip
                          key={key}
                          label={`${count} ${config.label}`}
                          size="small"
                          variant="outlined"
                          sx={{ 
                            fontWeight: 500,
                            height: 24,
                            color: 'text.secondary',
                          }}
                        />
                      );
                    })}
                </Stack>
              </Stack>
            </Box>

            {/* Tabs + Search Row */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              px: 2,
              gap: 2,
            }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  flex: 1,
                  minHeight: 48,
                  '& .MuiTabs-indicator': { 
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                  },
                  '& .MuiTab-root': {
                    minHeight: 48,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }
                }}
              >
                {Object.entries(CATEGORY_CONFIGS).map(([key, config]) => {
                  const Icon = config.icon;
                  return (
                    <Tab
                      key={key}
                      label={
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Icon sx={{ fontSize: 18 }} />
                          <span>{config.label}</span>
                          <Chip 
                            label={categoryCounts[key] || 0}
                            size="small"
                            sx={{ 
                              height: 20,
                              fontSize: '0.75rem',
                              fontWeight: 600,
                            }}
                          />
                        </Stack>
                      }
                      value={key}
                    />
                  );
                })}
              </Tabs>

              <TextField
                variant="outlined"
                size="small"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ 
                  width: 240,
                  '& .MuiOutlinedInput-root': {
                    height: 36,
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 20, color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery('')}
                        edge="end"
                        sx={{ width: 24, height: 24 }}
                      >
                        <ClearIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Box>
          </Paper>

          {/* Grid */}
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredActivities.length === 0 ? (
            <EmptyState category={activeTab} headerFilter={activeFilter} />
          ) : (
            <Fade in timeout={800}>
              <Grid container spacing={3}>
                {filteredActivities.map((activity, index) => (
                  <Grow
                    in
                    timeout={600 + index * 50}
                    key={`${activity.activity_type}-${activity.activity_id}`}
                  >
                    <Grid item xs={12} sm={6} md={4} lg={3}>
                      <ActivityCard activity={activity} />
                    </Grid>
                  </Grow>
                ))}
              </Grid>
            </Fade>
          )}
        </Box>
      </Fade>
    </Container>
  );
}